import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { ArrowLeft, Clock, Calendar, Phone, MessageCircle, Share2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extended article content
const articleContent = {
  "was-kostet-kfz-gutachten": {
    title: "Was kostet ein Kfz-Gutachten?",
    metaTitle: "Kfz Gutachten Kosten 2024 | Alle Infos zur Kostenübernahme",
    category: "Ratgeber",
    readTime: "5 Min.",
    publishedAt: "2024-02-23",
    image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200",
    excerpt: "Erfahren Sie alles über die Kosten eines Kfz-Gutachtens und wer diese übernimmt.",
    content: [
      {
        type: "intro",
        text: "Nach einem Unfall stellt sich oft die Frage: Was kostet ein Kfz-Gutachten und wer übernimmt die Kosten? In diesem Artikel erklären wir Ihnen alle wichtigen Details."
      },
      {
        type: "heading",
        text: "Wer zahlt das Gutachten?"
      },
      {
        type: "paragraph",
        text: "Bei einem unverschuldeten Unfall übernimmt die Haftpflichtversicherung des Unfallverursachers die Kosten für das Gutachten. Sie müssen also nichts bezahlen, wenn Sie nicht der Verursacher des Unfalls sind."
      },
      {
        type: "paragraph",
        text: "Dies ist in § 249 BGB geregelt: Der Schädiger muss den Zustand herstellen, der bestehen würde, wenn der Schaden nicht eingetreten wäre. Dazu gehören auch die Kosten für die Feststellung des Schadens."
      },
      {
        type: "heading",
        text: "Wie hoch sind die Kosten?"
      },
      {
        type: "paragraph",
        text: "Die Kosten für ein Kfz-Gutachten richten sich nach dem Schadensumfang. Je höher der Schaden, desto aufwändiger die Dokumentation und desto höher die Kosten. Als Richtwert können Sie mit 200 bis 800 Euro rechnen, bei Totalschäden auch mehr."
      },
      {
        type: "list",
        items: [
          "Bagatellschaden (unter 750€): Kostenvoranschlag ca. 50-100€",
          "Mittlerer Schaden (750-2.500€): Gutachten ca. 200-400€",
          "Größerer Schaden (2.500-10.000€): Gutachten ca. 400-600€",
          "Totalschaden: Gutachten ca. 500-800€+"
        ]
      },
      {
        type: "heading",
        text: "Wann brauche ich ein Gutachten?"
      },
      {
        type: "paragraph",
        text: "Ein Gutachten ist bei Schäden über der sogenannten Bagatellgrenze (ca. 750 Euro) sinnvoll und wird von den Versicherungen akzeptiert. Bei kleineren Schäden reicht oft ein Kostenvoranschlag einer Werkstatt."
      },
      {
        type: "tip",
        text: "Tipp: Lassen Sie sich bei einem unverschuldeten Unfall nicht von der gegnerischen Versicherung einen Gutachter aufdrängen. Sie haben das Recht auf einen eigenen, unabhängigen Gutachter!"
      }
    ]
  },
  "wertminderung-nach-unfall": {
    title: "Wertminderung nach Unfall: Ansprüche durchsetzen",
    metaTitle: "Wertminderung nach Unfall berechnen | So sichern Sie Ihre Ansprüche",
    category: "Recht",
    readTime: "7 Min.",
    publishedAt: "2024-02-20",
    image: "https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=1200",
    excerpt: "So berechnen Sie die Wertminderung Ihres Fahrzeugs nach einem Unfall und setzen Ihre Ansprüche durch.",
    content: [
      {
        type: "intro",
        text: "Nach einem Unfall verliert Ihr Fahrzeug an Wert – selbst wenn es vollständig repariert wurde. Diese merkantile Wertminderung können Sie sich von der gegnerischen Versicherung erstatten lassen."
      },
      {
        type: "heading",
        text: "Was ist merkantile Wertminderung?"
      },
      {
        type: "paragraph",
        text: "Die merkantile Wertminderung beschreibt den Minderwert eines Fahrzeugs, der trotz fachgerechter Reparatur verbleibt. Grund: Ein Käufer wird für ein Unfallfahrzeug weniger bezahlen als für ein unfallfreies Fahrzeug – selbst wenn der Schaden vollständig behoben wurde."
      },
      {
        type: "heading",
        text: "Wann steht mir Wertminderung zu?"
      },
      {
        type: "list",
        items: [
          "Sie waren unverschuldet in den Unfall verwickelt",
          "Das Fahrzeug ist nicht älter als 5-6 Jahre",
          "Die Laufleistung liegt unter 100.000 km",
          "Es handelt sich nicht um einen Bagatellschaden",
          "Das Fahrzeug war vor dem Unfall unfallfrei"
        ]
      },
      {
        type: "heading",
        text: "Wie wird die Wertminderung berechnet?"
      },
      {
        type: "paragraph",
        text: "Es gibt verschiedene Berechnungsmethoden. Am häufigsten wird die Methode nach Halbgewachs oder nach Ruhkopf/Sahm angewandt. Ein Sachverständiger ermittelt die Wertminderung im Rahmen des Unfallgutachtens."
      },
      {
        type: "tip",
        text: "Wichtig: Bestehen Sie auf die Berechnung der Wertminderung in Ihrem Gutachten. Viele Geschädigte verschenken hier bares Geld, weil sie ihre Ansprüche nicht kennen!"
      }
    ]
  },
  "fiktive-abrechnung-vs-reparatur": {
    title: "Fiktive Abrechnung vs. Reparatur: Was lohnt sich mehr?",
    metaTitle: "Fiktive Abrechnung oder Reparatur nach Unfall | Vor- und Nachteile",
    category: "Finanzen",
    readTime: "6 Min.",
    publishedAt: "2024-02-15",
    image: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1200",
    excerpt: "Welche Option lohnt sich mehr nach einem Unfall? Wir klären auf über die Vor- und Nachteile.",
    content: [
      {
        type: "intro",
        text: "Nach einem Unfall haben Sie die Wahl: Reparieren Sie Ihr Fahrzeug oder lassen Sie sich den Schaden auszahlen? Beide Optionen haben ihre Vor- und Nachteile."
      },
      {
        type: "heading",
        text: "Was ist eine fiktive Abrechnung?"
      },
      {
        type: "paragraph",
        text: "Bei der fiktiven Abrechnung verzichten Sie auf die Reparatur und lassen sich stattdessen den kalkulierten Netto-Reparaturbetrag auszahlen. Sie erhalten das Geld und können selbst entscheiden, was Sie damit machen."
      },
      {
        type: "heading",
        text: "Vorteile der fiktiven Abrechnung"
      },
      {
        type: "list",
        items: [
          "Schnelle Auszahlung ohne Reparaturwartezeit",
          "Freie Verwendung des Geldes",
          "Eigenreparatur oder günstigere Werkstatt möglich",
          "Bei älteren Fahrzeugen oft wirtschaftlich sinnvoller"
        ]
      },
      {
        type: "heading",
        text: "Nachteile der fiktiven Abrechnung"
      },
      {
        type: "list",
        items: [
          "Nur Netto-Betrag (ohne Mehrwertsteuer)",
          "Keine Ersatzwagenkosten",
          "Wertminderung schwerer durchsetzbar",
          "Schaden bleibt sichtbar bei Nicht-Reparatur"
        ]
      },
      {
        type: "heading",
        text: "Unsere Empfehlung"
      },
      {
        type: "paragraph",
        text: "Die Entscheidung hängt von Ihrer individuellen Situation ab. Bei neueren Fahrzeugen mit Wertminderungsanspruch ist die Reparatur oft sinnvoller. Bei älteren Fahrzeugen kann die fiktive Abrechnung die bessere Wahl sein."
      },
      {
        type: "tip",
        text: "Lassen Sie sich von uns beraten! Wir erklären Ihnen die Vor- und Nachteile beider Optionen in Bezug auf Ihre konkrete Situation."
      }
    ]
  }
};

const RatgeberDetailPage = () => {
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  
  useEffect(() => {
    axios.get(`${API}/blog`).then(res => setArticles(res.data)).catch(console.error);
  }, []);

  const article = articleContent[slug];

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-4">Artikel nicht gefunden</h1>
          <Link to="/ratgeber" className="text-[#AA6622] font-bold">Zurück zum Ratgeber</Link>
        </div>
      </div>
    );
  }

  const otherArticles = articles.filter(a => a.slug !== slug);

  return (
    <>
      <SEO 
        title={article.metaTitle || article.title}
        description={article.excerpt}
        canonical={`/ratgeber/${slug}`}
        keywords={`${article.category}, Kfz Gutachten, ${article.title}`}
        type="article"
      />
      {/* Breadcrumb */}
      <div className="bg-[#F7F7F7] py-4 border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#4A4A4A] hover:text-[#AA6622]">Startseite</Link>
            <span className="text-[#4A4A4A]">/</span>
            <Link to="/ratgeber" className="text-[#4A4A4A] hover:text-[#AA6622]">Ratgeber</Link>
            <span className="text-[#4A4A4A]">/</span>
            <span className="text-[#AA6622] font-medium truncate">{article.title}</span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <section className="bg-[#F7F7F7] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/ratgeber" className="inline-flex items-center text-[#AA6622] font-medium mb-6 hover:text-[#8e541b]">
            <ArrowLeft className="w-4 h-4 mr-2" />Alle Artikel
          </Link>
          <div className="flex items-center space-x-4 text-sm text-[#4A4A4A] mb-4">
            <span className="bg-[#AA6622]/10 text-[#AA6622] px-3 py-1 rounded-sm font-medium">{article.category}</span>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{article.readTime} Lesezeit</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>{new Date(article.publishedAt).toLocaleDateString('de-DE')}</span>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-6 font-['Outfit']">{article.title}</h1>
          <p className="text-xl text-[#4A4A4A] leading-relaxed">{article.excerpt}</p>
        </div>
      </section>

      {/* Featured Image */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <div className="aspect-video rounded-sm overflow-hidden shadow-lg">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Article Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            {article.content.map((block, index) => {
              switch (block.type) {
                case "intro":
                  return <p key={index} className="text-xl text-[#4A4A4A] leading-relaxed mb-8 font-medium">{block.text}</p>;
                case "heading":
                  return <h2 key={index} className="text-2xl font-bold text-black mt-12 mb-4 font-['Outfit']">{block.text}</h2>;
                case "paragraph":
                  return <p key={index} className="text-[#4A4A4A] leading-relaxed mb-6">{block.text}</p>;
                case "list":
                  return (
                    <ul key={index} className="space-y-2 mb-6">
                      {block.items.map((item, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <span className="w-2 h-2 bg-[#AA6622] rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-[#4A4A4A]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  );
                case "tip":
                  return (
                    <div key={index} className="bg-[#AA6622]/10 border-l-4 border-[#AA6622] p-6 my-8 rounded-r-sm">
                      <p className="text-[#4A4A4A] font-medium">{block.text}</p>
                    </div>
                  );
                default:
                  return null;
              }
            })}
          </div>

          {/* Author / CTA */}
          <div className="mt-16 p-8 bg-[#F7F7F7] rounded-sm">
            <h3 className="text-xl font-bold text-black mb-4 font-['Outfit']">Haben Sie Fragen?</h3>
            <p className="text-[#4A4A4A] mb-6">
              Wenn Sie weitere Fragen zu diesem Thema haben oder eine individuelle Beratung wünschen, kontaktieren Sie uns gerne.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
                <Phone className="w-5 h-5" /><span>0174 893 6514</span>
              </a>
              <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#20bd5a] transition-colors">
                <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {otherArticles.length > 0 && (
        <section className="py-16 bg-[#F7F7F7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-black mb-8 font-['Outfit']">Weitere Artikel</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherArticles.slice(0, 2).map((a) => (
                <Link key={a.id} to={`/ratgeber/${a.slug}`} className="bg-white rounded-sm overflow-hidden card-hover group flex">
                  <div className="w-1/3 overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="w-2/3 p-6">
                    <span className="text-[#AA6622] text-sm font-medium">{a.category}</span>
                    <h3 className="text-lg font-bold text-black mt-2 group-hover:text-[#AA6622] transition-colors font-['Outfit']">{a.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default RatgeberDetailPage;
